import React, { useState, useRef, useEffect } from 'react'

import { Play, Pause, Download, Edit, Trash2, X } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'

import { useTailwindDarkMode } from '@/shared/hooks/useTailwindDarkMode'
import { cn } from '@/shared/utils/helpers'

interface AudioPlayerProps {
	audioUrl: string
	audioBlob?: Blob
	fileName?: string
	onEdit?: () => void
	onDownload?: () => void
	onDelete?: () => void
	isRecording?: boolean
	isAbsolute?: boolean
	closable?: boolean
	onClose?: () => void
	closeIcon?: 'x' | 'trash'
	volume?: number
	hideHeader?: boolean
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
	audioUrl,
	audioBlob,
	fileName = 'generated_audio.mp3',
	onEdit,
	onDownload,
	onDelete,
	isRecording = false,
	isAbsolute = false,
	closable = false,
	onClose,
	closeIcon = 'x',
	volume,
	hideHeader = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(false)
	const [currentTime, setCurrentTime] = useState(0)
	const [duration, setDuration] = useState(0)
	const isDarkMode = useTailwindDarkMode()

	const waveformRef = useRef<HTMLDivElement>(null)
	const wavesurferRef = useRef<WaveSurfer | null>(null)

	useEffect(() => {
		if (!waveformRef.current) return

		// Mobile Safari (and some in-app WebViews) can be flaky with WaveSurfer's
		// WebAudio backend + blob: URLs. Using MediaElement and loadBlob is more reliable.
		const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
		const isIOS = /iPad|iPhone|iPod/.test(ua)

		const wavesurfer = WaveSurfer.create({
			container: waveformRef.current,
			waveColor: isDarkMode ? '#808080' : '#bababa',
			progressColor: isDarkMode ? '#ffffff' : 'hsl(var(--primary))',
			cursorColor: '#ffffff',
			cursorWidth: 0,
			barWidth: 2,
			barGap: 1,
			barRadius: 3,
			height: 48,
			normalize: true,
			...(isIOS ? {} : { backend: 'WebAudio' as const }),
			interact: true,
			hideScrollbar: true,
			barHeight: 1,
		})

		// Prefer loadBlob so we don't rely on fetch(blob:...) quirks and we preserve mime type.
		if (audioBlob) {
			wavesurfer.loadBlob(audioBlob)
		} else {
			wavesurfer.load(audioUrl)
		}

		wavesurfer.on('ready', () => {
			setDuration(wavesurfer.getDuration())
		})

		wavesurfer.on('error', (error) => {
			console.error('AudioPlayer - WaveSurfer error:', error)
		})

		wavesurfer.on('audioprocess', () => {
			setCurrentTime(wavesurfer.getCurrentTime())
		})

		wavesurfer.on('play', () => {
			setIsPlaying(true)
		})

		wavesurfer.on('pause', () => {
			setIsPlaying(false)
		})

		wavesurfer.on('finish', () => {
			setIsPlaying(false)
			wavesurfer.seekTo(0)
		})

		wavesurferRef.current = wavesurfer

		return () => {
			wavesurfer.pause()
			wavesurfer.destroy()
		}
	}, [audioUrl, audioBlob, isDarkMode])

	useEffect(() => {
		if (!wavesurferRef.current || volume === undefined) return
		const normalized = Math.max(0, Math.min(100, volume)) / 100
		wavesurferRef.current.setVolume(normalized)
	}, [volume])

	const togglePlayPause = () => {
		if (wavesurferRef.current) {
			wavesurferRef.current.playPause()
		}
	}

	const formatTime = (time: number): string => {
		if (isNaN(time)) return '0:00'
		const minutes = Math.floor(time / 60)
		const seconds = Math.floor(time % 60)
		return `${minutes}:${seconds.toString().padStart(2, '0')}`
	}

	return (
		<div
			className={cn(
				'w-full dark:bg-background bg-muted rounded-xl overflow-hidden',
				isAbsolute
					? 'fixed bottom-0 lg:left-20 right-0 z-10 max-w-6xl lg:mx-auto w-full lg:mb-2'
					: ''
			)}
		>
			{!hideHeader && (
				<div className="flex items-center justify-between px-4 py-1 border-b border-border">
					<h3 className="text-sm font-normal truncate flex-1 mr-3">
						{isRecording ? 'Your recording' : fileName}
					</h3>
					<div className="flex items-center gap-1">
						{onDownload && (
							<button
								onClick={onDownload}
								className="h-8 w-8 flex items-center justify-center transition-colors"
								title="Download audio"
							>
								<Download className="h-4 w-4" />
							</button>
						)}
						{onEdit && !isRecording && (
							<button
								onClick={onEdit}
								className="h-8 w-8 flex items-center justify-center transition-colors"
								title="Edit"
							>
								<Edit className="h-4 w-4" />
							</button>
						)}
						{closable && (
							<button
								onClick={onClose}
								className="h-8 w-8 flex items-center justify-center transition-colors"
								title="Close"
							>
								{closeIcon === 'trash' ? (
									<Trash2 className="h-4 w-4" />
								) : (
									<X className="h-4 w-4" />
								)}
							</button>
						)}
						{onDelete && isRecording && (
							<button
								onClick={onDelete}
								className="h-8 w-8 flex items-center justify-center transition-colors text-destructive hover:text-destructive/80"
								title="Delete recording"
							>
								<Trash2 className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>
			)}

			{/* Player controls and waveform */}
			<div className="flex items-center gap-3 px-4 py-3">
				{/* Play/Pause button */}
				<button
					onClick={togglePlayPause}
					className="h-12 w-12 rounded-full bg-accent-foreground text-secondary hover:bg-primary/90 flex items-center justify-center flex-shrink-0 transition-all"
				>
					{isPlaying ? (
						<Pause className="h-4 w-4 fill-current" />
					) : (
						<Play className="h-4 w-4 fill-current ml-0.5" />
					)}
				</button>

				{/* Time display */}
				<span className="text-xs tabular-nums flex-shrink-0 w-10">
					{formatTime(currentTime)}
				</span>

				{/* Waveform container */}
				<div ref={waveformRef} className="flex-1 cursor-pointer" />

				{/* Duration display */}
				<span className="text-xs text-muted-foreground tabular-nums flex-shrink-0 w-10 text-right">
					{formatTime(duration)}
				</span>
			</div>
		</div>
	)
}
